import { api } from "./api";
import { API_ENDPOINTS } from "../../utils/api-service";
import { isObjectRecord } from "../../utils/functions/api-errors";
import { unwrapApiData } from "./points-service-helpers";
import {
  ConfiguredEventPointsMessageTemplates,
  EventPointsMessageTemplates,
  PointCurrencyGender,
  PointCurrencyName,
  PointSettings,
  UpdatePointSettingsPayload,
} from "../../models/pointSettings";

const settingsTag = { type: "pointSettings" as const, id: "SETTINGS" };

const isPointCurrencyGender = (value: unknown): value is PointCurrencyGender =>
  value === "f" || value === "m";

const mapPointCurrencyName = (raw: unknown): PointCurrencyName | null => {
  if (
    !isObjectRecord(raw) ||
    typeof raw.singular !== "string" ||
    typeof raw.plural !== "string" ||
    !isPointCurrencyGender(raw.gender)
  ) {
    return null;
  }
  return { singular: raw.singular, plural: raw.plural, gender: raw.gender };
};

/**
 * `null` a nivel del objeto se normaliza a los 3 campos en `null`: para el formulario es lo
 * mismo "no configuró nada" que "configuró los 3 campos vacíos", y así no hay que chequear dos
 * formas distintas de lo mismo en el resto del código.
 */
const mapEventPointsMessageTemplates = (raw: unknown): ConfiguredEventPointsMessageTemplates | null => {
  if (raw === null) {
    return { titleTemplate: null, attendanceClauseTemplate: null, bonusClauseTemplate: null };
  }
  if (!isObjectRecord(raw)) return null;

  const titleTemplate =
    raw.title_template === null ? null : typeof raw.title_template === "string" ? raw.title_template : undefined;
  const attendanceClauseTemplate =
    raw.attendance_clause_template === null
      ? null
      : typeof raw.attendance_clause_template === "string"
      ? raw.attendance_clause_template
      : undefined;
  const bonusClauseTemplate =
    raw.bonus_clause_template === null
      ? null
      : typeof raw.bonus_clause_template === "string"
      ? raw.bonus_clause_template
      : undefined;

  if (titleTemplate === undefined || attendanceClauseTemplate === undefined || bonusClauseTemplate === undefined) {
    return null;
  }
  return { titleTemplate, attendanceClauseTemplate, bonusClauseTemplate };
};

/** A diferencia de la versión configurada, acá los 3 campos son obligatorios y no vacíos. */
const mapEffectiveEventPointsMessageTemplates = (raw: unknown): EventPointsMessageTemplates | null => {
  if (
    !isObjectRecord(raw) ||
    typeof raw.title_template !== "string" ||
    typeof raw.attendance_clause_template !== "string" ||
    typeof raw.bonus_clause_template !== "string"
  ) {
    return null;
  }
  return {
    titleTemplate: raw.title_template,
    attendanceClauseTemplate: raw.attendance_clause_template,
    bonusClauseTemplate: raw.bonus_clause_template,
  };
};

/**
 * Respuesta fuera de contrato (sin `effective_currency_name` válido, un `currency_name` que no es
 * null ni un nombre válido, un `rules_article_id` que no es null ni un id, o plantillas de
 * mensaje de puntos fuera de forma) → null. La pantalla la muestra como error: nunca arma un
 * formulario con valores inventados.
 */
export const mapPointSettings = (raw: unknown): PointSettings | null => {
  if (!isObjectRecord(raw)) return null;

  const effectiveCurrencyName = mapPointCurrencyName(raw.effective_currency_name);
  const currencyName = raw.currency_name === null ? null : mapPointCurrencyName(raw.currency_name);
  if (effectiveCurrencyName === null || (raw.currency_name !== null && currencyName === null)) {
    return null;
  }

  const rulesArticleId =
    raw.rules_article_id === null
      ? null
      : typeof raw.rules_article_id === "string"
      ? raw.rules_article_id
      : undefined;
  if (rulesArticleId === undefined) return null;

  const eventPointsMessageTemplates = mapEventPointsMessageTemplates(raw.event_points_message_templates);
  const effectiveEventPointsMessageTemplates = mapEffectiveEventPointsMessageTemplates(
    raw.effective_event_points_message_templates
  );
  if (eventPointsMessageTemplates === null || effectiveEventPointsMessageTemplates === null) {
    return null;
  }

  const referralShareText =
    raw.referral_share_text === null
      ? null
      : typeof raw.referral_share_text === "string"
      ? raw.referral_share_text
      : undefined;
  const effectiveReferralShareText =
    typeof raw.effective_referral_share_text === "string" ? raw.effective_referral_share_text : undefined;
  if (referralShareText === undefined || effectiveReferralShareText === undefined) {
    return null;
  }

  return {
    currencyName,
    effectiveCurrencyName,
    rulesArticleId,
    eventPointsMessageTemplates,
    effectiveEventPointsMessageTemplates,
    referralShareText,
    effectiveReferralShareText,
  };
};

export const pointSettingsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getPointSettings: build.query<PointSettings | null, void>({
      query: () => API_ENDPOINTS.POINTS.SETTINGS,
      transformResponse: (response: unknown) => mapPointSettings(unwrapApiData(response)),
      providesTags: [settingsTag],
    }),

    updatePointSettings: build.mutation<PointSettings | null, UpdatePointSettingsPayload>({
      query: ({ currencyName, rulesArticleId, eventPointsMessageTemplates, referralShareText }) => ({
        url: API_ENDPOINTS.POINTS.SETTINGS,
        method: "PUT",
        body: {
          currency_name: currencyName,
          rules_article_id: rulesArticleId,
          event_points_message_templates: {
            title_template: eventPointsMessageTemplates.titleTemplate,
            attendance_clause_template: eventPointsMessageTemplates.attendanceClauseTemplate,
            bonus_clause_template: eventPointsMessageTemplates.bonusClauseTemplate,
          },
          referral_share_text: referralShareText,
        },
      }),
      transformResponse: (response: unknown) => mapPointSettings(unwrapApiData(response)),
      invalidatesTags: [settingsTag],
    }),
  }),
});

export const { useGetPointSettingsQuery, useUpdatePointSettingsMutation } = pointSettingsApi;
